import { Fragment } from 'preact';
import { useRef, useCallback, useEffect } from 'preact/hooks';

import { noInputBlocks, typingTimeout } from '@/constants';
import { ExternalData } from '@/features/elements';
import { isLandingPage, makeClassName, rnd } from '@/utils';
import {
	useConfigStore,
	useFormProps,
	useBlockStore,
	useFormStore
} from '@/features/form';

import useConversation from '../hooks/useConversation';
import Message from './Message';
import Delay from './Delay';
import BlockInput from './BlockInput';
import { honeyBotTypeName, honeyBotId } from '@/constants';

const honeyBotBlock = {
	typeName: honeyBotTypeName,
	pageNumber: 0,
	title: 'What is your opinion?'
};

function getPreviousNoInputBlocks({ index, blockState, blocks, displayedBlocks }) {
	const slicedLayout = displayedBlocks.slice(0, index).reverse();
	let counter = 0;
	for (let i = 0; i < slicedLayout.length; i++) {
		const blockId = slicedLayout[i];
		const block = blocks[blockId];
		const state = blockState[blockId];
		//handle prefilled elements as no input elements also because they don't require user input anymore
		if (
			state.isVisible &&
			(noInputBlocks.indexOf(block.typeName) > -1 || state.wasPrefilled)
		) {
			counter++;
		} else {
			break;
		}
	}
	return counter;
}

function determineContainerStyles({
	webView,
	webViewShowHeader,
	formHeaderHeight,
	formOffset
} = {}) {
	if (webView && webViewShowHeader) {
		return {
			height: webViewShowHeader
				? `calc(100dvh - ${formHeaderHeight + formOffset}px)`
				: '100dvh'
		};
	}

	if (webView) {
		return null;
	}

	return {
		maxHeight: isLandingPage() ? '640px' : '520px',
		height: `calc(80vh - ${formHeaderHeight + formOffset}px)`
	};
}

export default function Conversation({ formHeaderHeight, formOffset, ...props }) {
	const blockState = useBlockStore();
	const { formType, formOpen } = useFormStore();
	const { webView, webViewShowHeader } = useFormProps();
	const { layout, blocks, properties } = useConfigStore();

	const { displayedBlocks, showableLayout } = useConversation();
	const container = useRef(null);
	const setScrollPosition = useCallback(() => {
		if (container.current) {
			container.current.scrollTop = container.current.scrollHeight;
		}
	}, []);

	const _id = useRef(rnd());

	useEffect(() => {
		const listener = () => {
			setTimeout(() => {
				try {
					container.current.scrollTop =
						document.activeElement.getBoundingClientRect().top;
				} catch (e) {}
			});
		};

		if (webView && window.visualViewport) {
			window.visualViewport.addEventListener('resize', listener);
			return () => window.visualViewport.removeEventListener('resize', listener);
		}
	}, [webView]);

	const startConversation = formOpen || formType === 'embed';

	//must be a little more than the typing animation
	const nextMessageDelay = Number(properties.typingTimeout ?? typingTimeout) * 1.2;

	return (
		<Fragment>
			<div
				className={makeClassName('conversation-container', webView ? 'webview' : null)}
				ref={container}
				style={determineContainerStyles({
					webView,
					webViewShowHeader,
					formHeaderHeight,
					formOffset
				})}
			>
				{displayedBlocks.map((blockId, elementIndex) => {
					const delay =
						getPreviousNoInputBlocks({
							index: elementIndex,
							blockState,
							blocks,
							displayedBlocks,
							layout: showableLayout
						}) * nextMessageDelay;

					return (
						<Delay delay={startConversation ? delay : null}>
							<Message
								blockId={blockId}
								prevBlockId={elementIndex !== 0 ? showableLayout[elementIndex - 1] : null}
								elementIndex={elementIndex}
								setScrollPosition={setScrollPosition}
							/>
						</Delay>
					);
				})}
			</div>
			{layout
				.filter(blockId => blocks[blockId].typeName === 'website_data')
				.map(websiteDataBlockId => {
					const state = blockState[websiteDataBlockId];
					const block = blocks[websiteDataBlockId];

					const setNestedValue = (datafieldId, value) =>
						blockState.actions.dispatch({
							type: 'set_nested_value',
							payload: {
								value,
								block,
								datafieldId,
								rules: [],
								layout
							}
						});

					return (
						<ExternalData
							block={block}
							state={state}
							setNestedValue={setNestedValue}
						/>
					);
				})}
			<div className={honeyBotTypeName}>
				<label for={`element-special_input-${blockState[honeyBotId].id}-${_id.current}`}>
					{honeyBotBlock.title}
				</label>
				<BlockInput
					block={honeyBotBlock}
					state={blockState[honeyBotId]}
					_id={_id.current}
					setHoneyBotValue={value =>
						blockState.actions.dispatch({
							type: 'set_honeybot_value',
							payload: { value }
						})
					}
				/>
			</div>
		</Fragment>
	);
}
